import { supabase } from '@/integrations/supabase/client';

export interface Plan {
  slug: string;
  nome: string;
  preco_mensal: number | null;
  preco_anual: number | null;
  limites: Record<string, number | null>;
  stripe_price_monthly_id: string | null;
  stripe_price_yearly_id: string | null;
}

/**
 * Lists all billing plans (Starter, Professional, Business, etc).
 * Ordered by monthly price ascending, nulls first (Starter's free tier leads).
 * Public reference data — not org-scoped.
 */
export async function listPlans(): Promise<Plan[]> {
  const { data, error } = await supabase
    .from('plans')
    .select('slug, nome, preco_mensal, preco_anual, limites, stripe_price_monthly_id, stripe_price_yearly_id')
    .order('preco_mensal', { ascending: true, nullsFirst: true });

  if (error) throw error;
  return data as Plan[];
}

/**
 * Creates a Stripe Checkout session via the `create-checkout-session` edge function.
 * Params use camelCase; the edge function expects snake_case — translation happens here.
 * Throws if the edge function errors OR if the response is missing the URL.
 */
export async function createCheckoutSession(params: {
  priceId: string;
  organizationId: string;
}): Promise<{ url: string }> {
  const { data, error } = await supabase.functions.invoke('create-checkout-session', {
    body: { price_id: params.priceId, organization_id: params.organizationId },
  });

  if (error) throw error;
  if (!data?.url) throw new Error('URL de checkout não retornada');

  return data as { url: string };
}

/**
 * Creates a Stripe Customer Portal session via the `create-portal-session` edge function.
 * Throws if the edge function errors OR if the response is missing the URL.
 */
export async function createPortalSession(params: {
  organizationId: string;
}): Promise<{ url: string }> {
  const { data, error } = await supabase.functions.invoke('create-portal-session', {
    body: { organization_id: params.organizationId },
  });

  if (error) throw error;
  if (!data?.url) throw new Error('URL do portal não retornada');

  return data as { url: string };
}

/**
 * Reads the current `plano` slug for the given organization.
 * Returns null if the row or field is null — throws on Supabase error.
 * Used by the checkout-polling hook to detect plan transitions post-checkout.
 */
export async function getOrganizationPlan(orgId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('organizations')
    .select('plano')
    .eq('id', orgId)
    .single();

  if (error) throw error;
  return data?.plano ?? null;
}
